import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import {
  LocataireSchema,
  type LocataireInsert,
  type LocataireRow,
  type LocataireUpdate,
} from "@/schemas/locataire";
import { z } from "zod";

/**
 * Repository des locataires d'un bien.
 *
 * Schéma :
 * - 1 locataire "actuel" max par bien (contrainte unique partielle en DB)
 * - N locataires "ancien" par bien (historique)
 *
 * Accès direct via .from() — RLS admin-only (migration 016).
 * Non-admins verront la feature plus tard via RPC.
 *
 * En mode mock (USE_SUPABASE=false), on ne fait rien — l'UI doit tolérer
 * une liste vide. Pas d'historique local pour l'instant.
 */

async function listFromSupabase(bienId: string): Promise<LocataireRow[]> {
  const { data, error } = await supabase
    .from("locataires")
    .select("*")
    .eq("bien_id", bienId)
    .order("type", { ascending: true })        // 'actuel' avant 'ancien'
    .order("dateFin", { ascending: false })    // anciens plus récents en haut
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listLocataires(${bienId}): ${error.message}`);
  return z.array(LocataireSchema).parse(data ?? []);
}

async function insertOnSupabase(payload: LocataireInsert): Promise<LocataireRow> {
  const { data, error } = await supabase
    .from("locataires")
    .insert(payload)
    .select()
    .single();
  if (error) throw new Error(`insertLocataire: ${error.message}`);
  return LocataireSchema.parse(data);
}

async function updateOnSupabase(id: string, patch: LocataireUpdate): Promise<LocataireRow> {
  const { data, error } = await supabase
    .from("locataires")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(`updateLocataire(${id}): ${error.message}`);
  return LocataireSchema.parse(data);
}

async function deleteOnSupabase(id: string): Promise<void> {
  const { error } = await supabase.from("locataires").delete().eq("id", id);
  if (error) throw new Error(`deleteLocataire(${id}): ${error.message}`);
}

export const locatairesRepository = {
  async list(bienId: string): Promise<LocataireRow[]> {
    if (!USE_SUPABASE) return [];
    return listFromSupabase(bienId);
  },

  async create(payload: LocataireInsert): Promise<LocataireRow> {
    if (!USE_SUPABASE) {
      return {
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...payload,
      };
    }
    return insertOnSupabase(payload);
  },

  async update(id: string, patch: LocataireUpdate): Promise<LocataireRow> {
    if (!USE_SUPABASE) {
      throw new Error("update locataire: mock mode non supporté");
    }
    return updateOnSupabase(id, patch);
  },

  async remove(id: string): Promise<void> {
    if (!USE_SUPABASE) return;
    return deleteOnSupabase(id);
  },

  /**
   * Transfère le locataire actuel en "ancien" + crée un nouveau slot
   * "actuel" vide, dans UNE transaction Postgres (RPC `transfer_locataire`,
   * migration 127 — corrige le constat #7 de l'audit 2026-09-10 : les 2
   * opérations étaient auparavant 2 requêtes séparées, un état intermédiaire
   * — locataire archivé mais aucun "actuel" créé — était possible si la 2e
   * échouait en cours de route).
   *
   * `archivedDateFin` : date de fin de bail à graver sur le locataire
   * archivé. Par défaut = today (bascule manuelle), mais l'archivage
   * automatique au passage LOUÉ → DISPONIBLE passe `finDeContrat` (la vraie
   * date de sortie).
   */
  async transfer(
    bienId: string,
    currentId: string | null,
    archivedDateFin?: string,
  ): Promise<LocataireRow> {
    if (!USE_SUPABASE) {
      // Mode mock : mêmes 2 appels non transactionnels qu'avant (le mode
      // mock ne modélise pas de transaction) — `update()` reste non supporté
      // en mock, comme avant cette migration.
      if (currentId) {
        await locatairesRepository.update(currentId, {
          type: "ancien",
          dateFin: archivedDateFin || new Date().toISOString().slice(0, 10),
        });
      }
      return locatairesRepository.create({
        bien_id: bienId,
        nom: "",
        email: "",
        telephone: "",
        type: "actuel",
        dateDebut: "",
        dateFin: "",
        loyerInitial: "",
        loyerActuel: "",
        chargesInitiales: "",
        chargesActuelles: "",
        indexBaseYear: "",
      });
    }
    const { data, error } = await supabase.rpc("transfer_locataire", {
      p_bien_id: bienId,
      p_current_locataire_id: currentId,
      p_archived_date_fin: archivedDateFin ?? null,
    });
    if (error) throw new Error(`transferLocataire(${bienId}): ${error.message}`);
    return LocataireSchema.parse(data);
  },
};
